import DigiobcanApi, {
  type NewsFeed,
  type NewsFeedColumn,
} from '../../../api/DigiobcanApi';
import { getNewsFeeds } from '../../../features/queries/getNewsFeeds';
import { useM } from '../../../lib/query';
import { useLogger } from '../../../providers/LoggerProvider';
import { __, declension, toastError, toastSuccess } from '../../../utils';
import type { StatusChipTone } from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNowStrict } from 'date-fns';
import { sk } from 'date-fns/locale';

/**
 * Kým niečo beží alebo čaká, stav zdrojov sa obnovuje takto často. Parser
 * zapisuje priebeh raz za sekundu, takže častejšie nemá zmysel.
 */
export const POLL_MS = 2000;

/**
 * O koľko smie plánovač zmeškať `next_run_at`, kým to administrácia ohlási.
 * Zdroje sa spracúvajú po jednom a eGov je pomalý, takže posledný zdroj
 * cyklu reálne štartuje aj desiatky minút po `next_run_at`.
 */
const SCHEDULER_GRACE_MS = 45 * 60 * 1000;

export const isBusy = (feed: NewsFeed) =>
  feed.last_status === 'running' || feed.last_status === 'queued';

export const relative = (iso: string) =>
  formatDistanceToNowStrict(new Date(iso), { addSuffix: true, locale: sk });

/** Najneskorší z ISO časov (ISO reťazce sa dajú radiť ako text). */
const latest = (
  feeds: NewsFeed[],
  key: 'last_success_at' | 'next_run_at',
): string | undefined =>
  feeds
    .map((f) => f[key])
    .filter((d): d is string => !!d)
    .sort()
    .pop();

/**
 * Stav zdroja v zozname aj v detaile. Čo práve beží, má prednosť; vypnutý
 * zdroj plánovač nesťahuje, takže jeho výsledok posledného behu už nič
 * nehovorí. Nezaradený sa sťahuje ďalej (Môj Prešov nemusí byť jediný
 * odberateľ), preto je až za chybou - tá je dôležitejšia.
 */
export const statusMeta = (
  feed: NewsFeed,
): { label: string; tone: StatusChipTone } => {
  if (feed.last_status === 'queued') return { label: __('Čaká'), tone: 'info' };
  if (feed.last_status === 'running') {
    return { label: __('Prebieha'), tone: 'info' };
  }
  if (!feed.enabled) return { label: __('Vypnutý'), tone: 'neutral' };
  if (feed.last_status === 'error')
    return { label: __('Chyba'), tone: 'error' };
  // Žiadna kategória Môj Prešov ho nepreberá - jeho novinky sa v appke
  // nezobrazujú. Zaradí sa v detaile kategórie (zdroje kategórie).
  if (!feed.mapped) return { label: __('Nezaradený'), tone: 'warning' };
  if (feed.last_status === 'ok' || feed.last_status === 'unchanged') {
    return { label: __('V poriadku'), tone: 'success' };
  }
  return { label: __('Ešte nebežal'), tone: 'neutral' };
};

const megabytes = (bytes = 0) =>
  `${(bytes / 1024 / 1024).toLocaleString('sk', {
    maximumFractionDigits: 1,
  })} MB`;

const count = (n = 0) => n.toLocaleString('sk');

/**
 * Priebeh bežiaceho importu ako veta a, kde sa dá, ako percento. Fázy sú tie,
 * ktoré parser naozaj robí; ukladanie v `news-creator` ide za odosielaním
 * a samostatne sa nehlási.
 */
export const feedProgress = (
  feed: NewsFeed,
): { text: string; percent?: number } | undefined => {
  if (feed.last_status === 'queued') {
    return { text: __('Čaká, kým ho parser začne sťahovať') };
  }
  if (feed.last_status !== 'running') return undefined;
  const p = feed.progress;
  switch (p?.phase) {
    case 'fetch':
      // Kým nepríde prvý bajt, zdroj ešte pripravuje odpoveď - eGov faktúry
      // (56 MB) generuje desiatky sekúnd. „Sťahuje sa 0 MB" tomu nehovorilo
      // nič, čas od začiatku behu áno.
      if (!p.bytes) {
        const seconds = feed.last_run_at
          ? Math.max(
              0,
              Math.round((Date.now() - Date.parse(feed.last_run_at)) / 1000),
            )
          : undefined;
        return {
          text:
            seconds !== undefined
              ? `${__('Čaká sa na odpoveď zdroja')} (${seconds} s)`
              : __('Čaká sa na odpoveď zdroja'),
        };
      }
      return {
        text: p.total
          ? `${__('Sťahuje sa')} ${megabytes(p.bytes)} ${__('z')} ${megabytes(p.total)}`
          : `${__('Sťahuje sa')} ${megabytes(p.bytes)}`,
        percent: p.total ? ((p.bytes ?? 0) / p.total) * 100 : undefined,
      };
    case 'parse':
      return { text: `${__('Spracúva sa')} ${megabytes(p.bytes)}` };
    case 'send':
      return p.total
        ? {
            text: `${__('Odosiela sa')} ${count(p.done)} / ${count(p.total)} ${declension(
              p.total,
              [__('novinka'), __('novinky'), __('noviniek')],
            )}`,
            percent: ((p.done ?? 0) / p.total) * 100,
          }
        : { text: __('Nič nové na odoslanie') };
    case 'delete':
      return { text: __('Mažú sa novinky, ktoré zo zdroja zmizli') };
    default:
      return { text: __('Začína sa') };
  }
};

/** Súhrn nad zoznamom zdrojov: posledná synchronizácia, meškanie, chyby. */
export const feedsSummary = (feeds: NewsFeed[]) => {
  const enabled = feeds.filter((f) => f.enabled);
  const planned = latest(enabled, 'next_run_at');
  const busy = feeds.some(isBusy);
  return {
    // Najnovší úspešný beh cez všetky zapnuté zdroje - „kedy naposledy sa
    // novinky naozaj stiahli".
    lastSync: latest(enabled, 'last_success_at'),
    // `next_run_at` zapisuje plánovač parsera pri každom cykle; keď je dávno
    // v minulosti, cyklus neprebehol - parser nebeží alebo sa zasekol. Kým
    // niečo čaká alebo beží, parser zjavne žije a hláška by len mýlila.
    overdue:
      !enabled.length || busy
        ? undefined
        : !planned
          ? ('never' as const)
          : Date.parse(planned) + SCHEDULER_GRACE_MS < Date.now()
            ? planned
            : undefined,
    errors: enabled.filter((f) => f.last_status === 'error').length,
    busy,
  };
};

/**
 * Spustenie importu: bez argumentu všetky zapnuté zdroje, s `feedId` jeden
 * (aj vypnutý). Import beží v parseri, mutácia ho len zaradí.
 */
export const useRunNewsFeeds = () => {
  const { logger } = useLogger();
  const queryClient = useQueryClient();

  return useM(
    (feedId?: number) =>
      DigiobcanApi.runNewsFeeds(
        {},
        feedId === undefined ? {} : { feed_id: feedId },
      ),
    {
      onSuccess: ({ queued }) => {
        toastSuccess(
          queued === 1
            ? __('Synchronizácia sa spustila.')
            : `${__('Synchronizácia sa spustila pre')} ${queued} ${declension(
                queued,
                [__('zdroj'), __('zdroje'), __('zdrojov')],
              )}.`,
        );
        queryClient.invalidateQueries({
          queryKey: [getNewsFeeds.toString()],
        });
      },
      onError: (e, feedId) => {
        logger.error('Error running news feeds', {
          action: 'runNewsFeeds',
          error: e,
          data: { feedId },
        });
        toastError(__('Synchronizáciu sa nepodarilo spustiť.'));
      },
    },
  );
};

export type ColumnRole = 'category' | 'date' | 'id' | 'title';

export const COLUMN_ROLE_LABEL: Record<ColumnRole, string> = {
  category: __('Kategória'),
  date: __('Dátum'),
  id: __('ID'),
  title: __('Titulok'),
};

const templateKeys = (template?: string) =>
  [...(template ?? '').matchAll(/\{([^{}]+)\}/g)].map(([, key]) => key);

/**
 * Časť zdroja, ktorú administrácia upravuje: šablóny, stĺpec s dátumom
 * a poradie stĺpcov v popise novinky.
 */
export type FeedDraft = {
  url: string;
  category_template: string;
  id_template: string;
  title_template: string;
  date_field: string;
  enabled: boolean;
  notify: boolean;
  /** Mená stĺpcov v poradí popisu; prázdne = podľa abecedy. */
  column_order: string[];
};

export const feedDraft = (feed: NewsFeed): FeedDraft => ({
  url: feed.url,
  category_template: feed.category_template,
  id_template: feed.id_template ?? '',
  title_template: feed.title_template ?? '',
  date_field: feed.date_field ?? '',
  enabled: feed.enabled,
  notify: feed.notify,
  column_order: feed.column_order ?? [],
});

const SK_COLLATOR = new Intl.Collator('sk');

/**
 * Stĺpce zdroja v poradí, v akom ich `news-feed-parser` dá do popisu
 * novinky (`orderedEntries` v jeho `text.ts`): najprv uložené poradie, za
 * ním zvyšok podľa slovenskej abecedy. Bez uloženého poradia je to abeceda
 * celá - tak parser popis skladal vždy, nie v poradí zdroja, a tabuľka
 * v detaile musí ukazovať to, čo v popise naozaj je.
 */
export const orderedColumns = (
  columns: NewsFeedColumn[],
  order: string[],
): NewsFeedColumn[] => {
  const byName = new Map(columns.map((c) => [c.name, c]));
  const first = [...new Set(order)].flatMap((name) => byName.get(name) ?? []);
  const placed = new Set(first.map((c) => c.name));
  return [
    ...first,
    ...columns
      .filter((c) => !placed.has(c.name))
      .sort((a, b) => SK_COLLATOR.compare(a.name, b.name)),
  ];
};

/** Polia, ktorých zmena sa na novinkách prejaví až synchronizáciou. */
const TEMPLATE_KEYS = [
  'url',
  'category_template',
  'id_template',
  'title_template',
  'date_field',
] as const;

/**
 * Len to, čo sa v návrhu zmenilo. API zmenu šablón hneď synchronizuje, kým
 * samotné vypnutie či stíšenie nie - poslať všetko by synchronizovalo vždy.
 */
export const draftChanges = (
  draft: FeedDraft,
  saved: FeedDraft,
): Partial<FeedDraft> => {
  const changes: Partial<FeedDraft> = {};
  for (const key of TEMPLATE_KEYS) {
    if (draft[key].trim() !== saved[key]) changes[key] = draft[key].trim();
  }
  if (draft.enabled !== saved.enabled) changes.enabled = draft.enabled;
  if (draft.notify !== saved.notify) changes.notify = draft.notify;
  if (draft.column_order.join('\n') !== saved.column_order.join('\n')) {
    changes.column_order = draft.column_order;
  }
  return changes;
};

/**
 * Či sa mení niečo, čo sa na novinkách prejaví až synchronizáciou. Poradie
 * stĺpcov tiež - mení popis uložených noviniek, a API preto zdroj po zmene
 * hneď synchronizuje.
 */
export const changesResync = (changes: Partial<FeedDraft>) =>
  TEMPLATE_KEYS.some((key) => key in changes) || 'column_order' in changes;

/**
 * Na čo sa stĺpec v zdroji používa. U eGovu to určujú šablóny (aj
 * neuložené, preto nad `FeedDraft`), u RSS je to pevné - tak, ako RSS
 * položku číta `news-feed-parser`.
 */
export const columnRoles = (
  type: NewsFeed['type'],
  draft: FeedDraft,
): Map<string, ColumnRole[]> => {
  const roles = new Map<string, ColumnRole[]>();
  const add = (name: string, role: ColumnRole) =>
    roles.set(name, [...(roles.get(name) ?? []), role]);

  if (type === 'RSS') {
    add('title', 'title');
    add('isoDate', 'date');
    add('id', 'id');
    return roles;
  }
  templateKeys(draft.category_template).forEach((k) => add(k, 'category'));
  templateKeys(draft.id_template).forEach((k) => add(k, 'id'));
  templateKeys(draft.title_template).forEach((k) => add(k, 'title'));
  if (draft.date_field) add(draft.date_field, 'date');
  return roles;
};

/** Zlúči medzery a odstráni oddeľovače, ktoré po vybratom stĺpci ostali visieť. */
const tidy = (template: string) =>
  template
    .replace(/\s{2,}/g, ' ')
    .replace(/([,:;|])\s*(?=[,:;|])/g, '')
    .replace(/^[\s,:;|]+|[\s,:;|]+$/g, '');

/**
 * Prepne rolu stĺpca a vráti upravený návrh. Šablóny sa upravujú tak, aby
 * výsledok bol čitateľný aj bez dolaďovania, ale doladiť sa dajú v poliach
 * nad tabuľkou - napr. oddeľovače v titulku.
 *
 *  * ID: stĺpce v poradí zo zdroja spojené `|` (stabilný kľúč, nie text),
 *  * titulok: stĺpec sa pripojí na koniec, pri vypnutí vypadne aj s tým, čo
 *    po ňom ostalo visieť,
 *  * kategória: stĺpec je ďalšia úroveň cesty (`... / {Stĺpec}`),
 *  * dátum: práve jeden stĺpec.
 */
export const toggleColumnRole = (
  draft: FeedDraft,
  role: ColumnRole,
  column: string,
  columnOrder: string[],
): FeedDraft => {
  const token = `{${column}}`;
  switch (role) {
    case 'date':
      return {
        ...draft,
        date_field: draft.date_field === column ? '' : column,
      };
    case 'id': {
      const keys = templateKeys(draft.id_template);
      const next = keys.includes(column)
        ? keys.filter((k) => k !== column)
        : [...keys, column];
      const rank = (k: string) => {
        const i = columnOrder.indexOf(k);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
      };
      return {
        ...draft,
        id_template: next
          .sort((a, b) => rank(a) - rank(b))
          .map((k) => `{${k}}`)
          .join('|'),
      };
    }
    case 'title':
      return {
        ...draft,
        title_template: draft.title_template.includes(token)
          ? tidy(draft.title_template.split(token).join(''))
          : draft.title_template.trim()
            ? `${draft.title_template.trim()} ${token}`
            : token,
      };
    case 'category': {
      const segments = draft.category_template
        .split(' / ')
        .map((segment) => segment.trim())
        .filter(Boolean);
      const has = segments.some(
        (segment) => segment === token || segment === `...${token}`,
      );
      return {
        ...draft,
        category_template: has
          ? segments
              .filter(
                (segment) => segment !== token && segment !== `...${token}`,
              )
              .join(' / ')
          : [...segments, token].join(' / '),
      };
    }
  }
};
